import { Injectable } from "@angular/core";
import { Observable, BehaviorSubject, throwError, from, EMPTY } from "rxjs";
import { catchError, filter, take, switchMap, tap } from "rxjs/operators";
import { HttpRequest, HttpHandler, HttpInterceptor, HttpEvent, HttpResponse, HttpContextToken } from "@angular/common/http";

import { AuthService } from "../services/auth.service";
import { SessionConfirmationService } from "../services/session-confirmation.service";
import { SharedDataService } from "./../services/shared-data.service";
import { ApplicationConstants } from "./../constants/application.constants";
import { detect } from "detect-browser";

/**
 * Opt-out, set via `HttpContext` (never sent over the wire), from this
 * interceptor's 401 -> refresh-token -> extension-fallback -> logout
 * escalation. Default `false` for every request — behavior against
 * `environment.API_URL` (Laravel) is completely unchanged.
 *
 * Use this ONLY for a request whose 401 does not prove the FlyMSG session
 * itself is dead — e.g. a call to flymsg-node (issue #582/AI-27), which
 * validates the bearer independently of Laravel and can reject a token
 * Laravel still accepts (mid-rollout, a route not yet wired, a scope the
 * caller doesn't have). A 401 from that call must degrade the calling
 * feature, never trigger a Laravel refresh attempt (and, on failure, a full
 * app logout) over what may be a node-only hiccup. Mirrors
 * flymsg-admin-fe's identical `SKIP_SESSION_LOGOUT_ON_401` token (PR #103,
 * AI-34) and flymsg-cmc-fe's/flymsg-roleplay's own copies.
 */
export const SKIP_SESSION_LOGOUT_ON_401 = new HttpContextToken<boolean>(() => false);

/**
 * Decode a JWT and return its `exp` (in seconds since epoch), or null if the
 * token is malformed. We don't need signature verification — the API does that.
 */
function readJwtExp(token: string): number | null {
  try {
    const payload = token.split(".")[1];
    if (!payload) return null;
    const json = atob(payload.replace(/-/g, "+").replace(/_/g, "/"));
    const decoded = JSON.parse(json);
    return typeof decoded?.exp === "number" ? decoded.exp : null;
  } catch {
    return null;
  }
}

function isJwtStillValid(token: string | null | undefined): boolean {
  if (!token) return false;
  const exp = readJwtExp(token);
  if (exp === null) return false;
  // 30s leeway — avoid handing back a token that's about to expire mid-request.
  return exp * 1000 > Date.now() + 30_000;
}

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  headers: any;
  private refreshTokenInProgress = false;
  private extensionFallbackAttempted = false;
  // Refresh Token Subject tracks the current token, or is null if no token is currently
  // available (e.g. refresh pending). Replaced with a fresh subject after a hard
  // failure so a subsequent sign-in in the same SPA instance starts clean.
  private refreshTokenSubject: BehaviorSubject<any> = new BehaviorSubject<any>(null);

  constructor(
    private readonly auth: AuthService,
    private readonly sharedData: SharedDataService,
    // Issue #564 (round 2): providedIn: "root" — a true app-wide singleton,
    // unlike AuthService (module-provided via CoreModule, instantiated once
    // per injector). See session-confirmation.service.ts.
    private readonly sessionConfirmation: SessionConfirmationService
  ) {}

  addAccessToken(req: HttpRequest<any>, res?: any) {
    let ulrHeards = req.url.includes("v1/user/tracking-extension");
    let hasImportCsvUrl = req.url.includes("v1/admin-center") && req.url.includes("users/import-csv");
    this.headers = {
      accept: req.body && req.body.isExternalRequest ? "*/*" : ApplicationConstants.ACCEPT,
    };

    if (ulrHeards) {
      this.headers["Content-Type"] = "application/x-www-form-urlencoded; charset=UTF-8";
    } else if (!hasImportCsvUrl) {
      this.headers["Content-Type"] = ApplicationConstants.CONTENT_TYPE;
    }

    // Get the auth token from the service.
    const accessToken = res?.access_token ? res.access_token : this.auth.getAccessToken();

    // conditionally add Authorization header
    // if noCredentials is true means we do not send the accessToken with API.
    // When a fresh token is explicitly passed (res.access_token, e.g. after a token refresh),
    // always override the Authorization header so the retry doesn't reuse the expired token.
    // Otherwise, preserve any Authorization header already on the request (e.g. masquerade token).
    const hasFreshToken = !!res?.access_token;
    if (accessToken && (hasFreshToken || !req.headers.has('Authorization')) && (!req.body || (req.body && !req.body.noCredentials))) {
      this.headers.Authorization = "Bearer " + accessToken;
    }

    if (req.body) {
      delete req.body.noCredentials;
    }

    // Clone the request and replace the original headers with
    // cloned headers, updated with the authorization.
    return req.clone({
      setHeaders: this.headers,
      body: req.body && req.body.value ? req.body.value : req.body,
    });
  }

  /**
   * Issue #564 QA round 1 fix: auth/refresh, auth/login, and masquerade/start
   * are the same URLs the catchError branch below already excludes from the
   * retry path (they get a plain throwError instead). They must be excluded
   * here too: flymsg-be's LoginController::refresh() answers an INVALID
   * refresh token with HTTP 200 and an error JSON body (it json-encodes
   * whatever Passport returns, and an error body still counts as a 2xx
   * `HttpResponse`), and addAccessToken puts an Authorization header on this
   * request too — so without this exclusion a failed refresh round-trip
   * would itself "confirm" a session that was never actually valid.
   */
  private isSessionConfirmationExemptUrl(url: string): boolean {
    return url.includes("auth/refresh") || url.includes("auth/login") || url.includes("masquerade/start");
  }

  /**
   * Issue #564: routes a request through `next.handle`, and — the moment it
   * resolves with a 2xx `HttpResponse` while carrying an `Authorization`
   * header for a non-exempt URL (see {@link isSessionConfirmationExemptUrl})
   * — tells SessionConfirmationService the session is server-confirmed. Used for the
   * original request AND every retry this interceptor issues itself (post
   * token-refresh, post extension-token-recovery), so a session that only
   * becomes valid after a 401->refresh round-trip still confirms via the
   * RETRIED original request — not the refresh call itself.
   */
  private handleWithSessionConfirmation(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    return next.handle(request).pipe(
      tap((event: HttpEvent<any>) => {
        if (
          event instanceof HttpResponse &&
          event.ok &&
          request.headers.has("Authorization") &&
          !this.isSessionConfirmationExemptUrl(request.url)
        ) {
          this.sessionConfirmation.confirmSession();
        }
      })
    );
  }

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    const request = this.addAccessToken(req);

    // send cloned request with header to the next handler.
    return this.handleWithSessionConfirmation(request, next).pipe(
      catchError((error: any) => {
        // We don't want to refresh token for some requests like login or refresh token itself
        // So we verify url and we throw an error if it's the case
        if (request.url.includes("auth/refresh") || request.url.includes("auth/login") || request.url.includes("masquerade/start")) {
          // When the refresh request itself fails, reset the flag so the inner
          // catchError (below) can handle the extension-token fallback once.
          if (request.url.includes("refresh")) {
            this.refreshTokenInProgress = false;
          }

          if (error.status === 422) {
            error.error.error = error.error.message ?? error.error.error_description;
          }

          return throwError(error);
        }

        // A request marked SKIP_SESSION_LOGOUT_ON_401 (flymsg-node calls, see
        // the token's doc comment above) never triggers the refresh/logout
        // escalation below — its 401 does not prove the FlyMSG session
        // itself is dead.
        if (error.status === 401 && request.context.get(SKIP_SESSION_LOGOUT_ON_401)) {
          return throwError(() => error);
        }

        // If error status is different than 401 we want to skip refresh token
        // So we check that and throw the error if it's the case
        if (error.status === 401) {
          if (this.extensionFallbackAttempted) {
            // Extension fallback was already tried on this request — go straight to logout.
            // Return EMPTY to swallow the error after redirect; the user is navigated away
            // so surfacing an "Unauthorized" toast here is misleading and confusing.
            this.auth.redirectAfterLogout();
            return EMPTY;
          }
          if (this.refreshTokenInProgress) {
            // If refreshTokenInProgress is true, we will wait until refreshTokenSubject has a non-null value
            // – which means the new token is ready and we can retry the request again.
            // If refresh fails the subject is errored, which propagates to every queued request
            // so they reject instead of hanging forever (which would freeze the UI on a white screen).
            return this.refreshTokenSubject.pipe(
              filter((result) => result !== null),
              take(1),
              switchMap((result) => this.handleWithSessionConfirmation(this.addAccessToken(request, result), next))
            );
          } else {
            this.refreshTokenInProgress = true;

            // Set the refreshTokenSubject to null so that subsequent API calls will wait until the new token has been retrieved
            this.refreshTokenSubject.next(null);

            // Call auth.refreshAccessToken(this is an Observable that will be returned)
            return this.auth.refreshAccessToken().pipe(
              switchMap((response: any) => {
                const result = response ? response.result : undefined;
                if (result?.access_token) {
                  this.refreshTokenInProgress = false;
                  this.extensionFallbackAttempted = false;
                  this.refreshTokenSubject.next(result);

                  this.sharedData.setAttribute("accessToken", result.access_token);
                  this.sharedData.setAttribute("refreshToken", result.refresh_token);

                  const userDetail = this.auth.userDetails;

                  this.auth.saveUserDetail({
                    ...userDetail,
                    access_token: result.access_token,
                    refresh_token: result.refresh_token,
                  });

                  return this.handleWithSessionConfirmation(this.addAccessToken(request, result), next);
                } else {
                  // Refresh succeeded but returned no access_token — try extension fallback
                  this.refreshTokenInProgress = false;
                  this.extensionFallbackAttempted = true;
                  return this.tryExtensionTokenFallback(request, next, error);
                }
              }),
              catchError((err: any) => {
                // Refresh token request failed — try extension token as fallback before signing out
                this.refreshTokenInProgress = false;
                this.extensionFallbackAttempted = true;
                return this.tryExtensionTokenFallback(request, next, err);
              })
            );
          }
        } else {
          return throwError(error);
        }
      })
    );
  }

  /**
   * Attempts to recover a session by requesting the current access token from
   * the browser extension (via the "getAccessToken" message).  If the extension
   * returns a token the original request is retried with it; otherwise the user
   * is redirected to sign-in WITHOUT sending a logout message to the extension
   * (the extension is still signed in — only the app session has expired).
   */
  private tryExtensionTokenFallback(
    originalRequest: HttpRequest<any>,
    next: HttpHandler,
    error: any
  ): Observable<HttpEvent<any>> {
    return from(this.auth.getAccessTokenFromExtension()).pipe(
      switchMap((extensionToken: string | null) => {
        // The extension may hand us a token whose JWT exp has already passed
        // (its in-memory check is best-effort, and a stale value in storage
        // can leak through). Verify locally before trusting it — replaying
        // an expired token would 401 again, loop back into this interceptor,
        // and only stop after `extensionFallbackAttempted` short-circuits
        // to logout. Better to reject up-front and head straight to /signin.
        if (isJwtStillValid(extensionToken)) {
          this.sharedData.setAttribute("accessToken", extensionToken);
          const userDetail = this.auth.userDetails;
          this.auth.saveUserDetail({
            ...userDetail,
            access_token: extensionToken,
          });
          const result = { access_token: extensionToken };
          this.refreshTokenSubject.next(result);
          return this.handleWithSessionConfirmation(this.addAccessToken(originalRequest, result), next);
        } else {
          // No token from extension either — sign out the app only (not the extension).
          // Return EMPTY to swallow the error; surfacing "Unauthorized" after redirect is misleading.
          this.auth.redirectAfterLogout();
          return EMPTY;
        }
      })
    );
  }

  /**
   * When refresh + extension fallback both fail, the BehaviorSubject is still
   * sitting at `null`, so every request that parked on it via filter(r=>r!==null)
   * waits forever. Erroring the subject propagates to all subscribers (their
   * pipelines will surface the error to handlers / route resolvers), and we
   * replace it with a fresh subject for any future authenticated session in
   * the same SPA instance.
   */
  private failQueuedRequests(error: any): void {
    try {
      this.refreshTokenSubject.error(error);
    } catch {
      // Subject may already be in errored state if multiple parallel 401s
      // hit this path — that's fine, the queue is already drained.
    }
    this.refreshTokenSubject = new BehaviorSubject<any>(null);
    this.refreshTokenInProgress = false;
    this.extensionFallbackAttempted = false;
  }
}
